// lib
import React, { useContext, useState, useRef, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'

import ADND from '@aytm/lib/adnd/ADND'
import { DataContext } from '@aytm/app/stats/page/question-views/components/mapping/Component'

import { generateUID, encodeText, decodeText } from '@aytm/app/stats/lib/helpers'

// components
import { InputText } from 'primereact/inputtext'
import { Dropdown } from 'primereact/dropdown'

import { itemTemplate, valueTemplate, optionGroupTemplate } from '@aytm/app/stats/page/components/lib/templates'

// styles
import style from '@aytm/app/stats/page/question-views/components/mapping/styles/Component.module.scss'

const Variable = ({ item, position, dndItemsList }) => {
  const { id, data, disabled, dispatch } = useContext(DataContext)

  const [selectedValue, setSelectedValue] = useState(null)

  const changedRef = useRef(false)

  const dndListItemRef = useRef()
  const dndListItemHandleRef = useRef()

  const selectedValueRef = useRef(null)
  selectedValueRef.current = selectedValue

  const fetchData = useCallback(() => {
    return {
      id,
      item,
      position: position - 1,
      windowUID: data.windowUID,
      remove: () =>
        dispatch({
          type: 'DELETE_VARIABLE',
          data: position - 1
        })
    }
  }, [item, position, id, data.windowUID, dispatch])

  const dndListItem = ADND.targets.itemsList.listItem({
    type: `dlq-variable-${data.windowUID}`,
    list: dndItemsList
  })
  const dndListItemHandle = ADND.sources
    .listItemHandle({
      type: `dlq-variable-${data.windowUID}`,
      item: dndListItem,
      cloneable: true
    })
    .fetchData(fetchData)

  const onSelectedChange = (value = null, _values) => {
    const group = data.filters.find(v => v.items.some(v => v.key == value)) || null
    if (null !== group && group.disabled) return

    const option = group.items.find(v => v.key == value) || null
    if (null !== option && option.disabled) return

    setSelectedValue(value)

    changedRef.current = true

    const options =
      null === option || option.options == undefined
        ? []
        : option.options.filter(
            _v => null !== _v.pattern && _v.pattern != undefined && 0 < _v.pattern.length && !_v.other
          )

    dispatch({
      type: 'UPDATE_VARIABLE',
      data: {
        item: {
          ...item,
          text: option.text,
          pattern: null === option ? '' : value,
          rules:
            null === option || option.options == undefined
              ? []
              : option.options.map(v =>
                  v.other
                    ? 0 < options.length
                      ? `not (${options.map(_v => `(${_v.pattern})`).join(' or ')})`
                      : ''
                    : v.pattern
                )
        },
        position: position - 1
      }
    })
  }

  const onLabelChange = event => {
    dispatch({
      type: 'UPDATE_VARIABLE',
      data: {
        item: {
          ...item,
          text: encodeText(event.target.value)
        },
        position: position - 1
      }
    })
  }

  const onDeleteClick = () => {
    dispatch({
      type: 'DELETE_VARIABLE',
      data: position - 1
    })
  }

  const onHandleClick = event => {
    if (2 != event.detail) return

    dispatch({
      type: 'ADD_VARIABLE',
      data: {
        item: {
          ...item,
          key: generateUID()
        },
        position: position
      }
    })
  }

  useEffect(() => {
    if (dndListItem) dndListItem.connect(dndListItemRef.current)

    return () => {
      if (dndListItem) dndListItem.disconnect()
    }
  }, [dndListItem])

  useEffect(() => {
    if (dndListItemHandle) dndListItemHandle.connect(dndListItemHandleRef.current)

    return () => {
      if (dndListItemHandle) dndListItemHandle.disconnect()
    }
  }, [dndListItemHandle])

  useEffect(() => {
    const patternRefs = RegExp(/^(vq\d+)/i).test(item.pattern) ? [item.pattern] : []

    dispatch({
      type: 'UPDATE_REFS',
      data: {
        id: `v${position}`,
        refs: [patternRefs, [], []]
      }
    })
  }, [item.pattern, position, dispatch])

  useEffect(() => {
    if (changedRef.current) {
      changedRef.current = false
      return
    }

    const option =
      data.filters
        .flatMap(v => v.items)
        .find(v => String(v.value || v.key).toLowerCase() == item.pattern.toLowerCase()) || null

    if (null === option) {
      setSelectedValue(null)
    } else {
      setSelectedValue(option.key)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.pattern])

  useEffect(
    () => {
      if (null !== selectedValueRef.current) {
        const option =
          data.filters
            .flatMap(v => v.items)
            .find(v => String(v.value || v.key).toLowerCase() == selectedValueRef.current.toLowerCase()) || null

        if (null === option || option.options == undefined) setSelectedValue(null)
        else {
          const options = option.options.filter(
            _v => null !== _v.pattern && _v.pattern != undefined && 0 < _v.pattern.length && !_v.other
          )

          dispatch({
            type: 'UPDATE_VARIABLE',
            data: {
              item: {
                ...item,
                rules: option.options.map(v =>
                  v.other
                    ? 0 < options.length
                      ? `not (${options.map(_v => `(${_v.pattern})`).join(' or ')})`
                      : ''
                    : v.pattern
                )
              },
              position: position - 1
            }
          })
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data.filters]
  )

  const renderOptions = () => (
    <Dropdown
      placeholder="Select..."
      value={selectedValue}
      options={data.filters}
      optionValue="key"
      optionLabel="text"
      optionDisabled="disabled"
      optionGroupLabel="text"
      optionGroupChildren="items"
      itemTemplate={itemTemplate}
      valueTemplate={valueTemplate}
      optionGroupTemplate={optionGroupTemplate}
      filter={true}
      filterBy="search,text"
      filterPlaceholder="Search items"
      resetFilterOnHide={true}
      filterInputAutoFocus={false}
      fixedDropdownDir="bottom"
      disabled={disabled}
      onChange={disabled ? null : event => onSelectedChange(event.value)}
    />
  )

  return (
    <div ref={dndListItemRef} className={`PRAYTMTheme ${style.row} ${style['row-av']} p-fluid p-col-12`}>
      {disabled ? null : (
        <span className={style.delete} onClick={onDeleteClick}>
          <i className="icon-aytmfd-delete" />
        </span>
      )}
      <div className={`${style.index} p-field`}>{position}.</div>
      <div
        className={`${style.group} ${style['group-av']}${
          disabled ? ' ' + style['group-av-disabled'] : ''
        } p-fluid p-field p-col`}
      >
        <div className={`p-field p-col-8`}>{renderOptions()}</div>
        <div className={`p-field p-col`}>
          <InputText
            placeholder="Please type a label..."
            value={decodeText(item.text)}
            maxLength={120}
            disabled={disabled}
            onChange={disabled ? null : onLabelChange}
          />
        </div>
      </div>
      {disabled ? null : (
        <div className="p-field">
          <span ref={dndListItemHandleRef} className={style.handle} onClick={onHandleClick}>
            <i className="icon-aytmfd-handle" />
          </span>
        </div>
      )}
    </div>
  )
}

Variable.propTypes = {
  item: PropTypes.object,
  position: PropTypes.number,
  dndItemsList: PropTypes.any
}

Variable.displayName = 'Variable'

export default Variable
